import React, { useState, useEffect, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Glitch from 'glitch-javascript-sdk';
import { Button, Card, Container, Col, Row, Alert, Modal } from 'react-bootstrap';
import Footer from '../../component/layout/footer';
import GameTitleForm from '../../../marketing/component/section/titles/title_form';
import Loading from '../../component/alerts/Loading';
import timeouts from '../../../../constants/timeouts';
import Danger from '../../component/alerts/Danger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGamepad, faLock, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import Navigate from '../../../../util/Navigate';

const GameShowRegisterGameStandalonePage = () => {
    const [show, setShow] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [isRegistered, setIsRegistered] = useState(false);
    const [gameTitle, setGameTitle] = useState({});
    const [gameMainImageBlob, setMainImageBlob] = useState(null);
    const [gameBannerImageBlob, setBannerImageBlob] = useState(null);
    const [externalGameTitle, setExternalGameTitle] = useState(null);
    const [errors, setErrors] = useState({});
    const [titleErrors, setTitleErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const { id } = useParams();
    const navigate = useNavigate();
    const [showLoginModal, setShowLoginModal] = useState(false);

    useEffect(() => {
        if (!Glitch.util.Session.isLoggedIn() && typeof window !== 'undefined') {
            setShowLoginModal(true);
        }
        // Fetch game show details
        Glitch.api.GameShows.view(id)
            .then(response => setShow(response.data.data))
            .catch(error => console.error('Error fetching show:', error));
    }, [id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        Glitch.api.Titles.create(gameTitle)
            .then(response => Glitch.api.GameShows.registerTitle(id, { title_id: response.data.data.id }))
            .then(() => {
                setSuccessMessage('Your game has been successfully registered!');
                setIsRegistered(true);
                setTimeout(() => setSuccessMessage(''), timeouts.success_message_timeout);
            })
            .catch(error => {
                setTitleErrors(error?.response?.data || {});
                setTimeout(() => setTitleErrors({}), timeouts.error_message_timeout);
            })
            .finally(() => setIsLoading(false));
    };

    const handleLoginRedirect = () => {
        const currentPath = window.location.pathname;
        navigate(Navigate.authLogin() + `?redirect=${encodeURIComponent(currentPath)}`);
    };

    const handleRegisterRedirect = () => {
        const currentPath = window.location.pathname;
        navigate(Navigate.authRegister() +`?redirect=${encodeURIComponent(currentPath)}`);
    };

    return (
        <Fragment>
            <Container className="register-section" style={{ marginTop: '2rem', maxWidth: '1200px' }}>
                <Row className="justify-content-center">
                    <Col md={10}>
                        <Card className="shadow-lg mb-4" style={{ position: 'relative', overflow: 'hidden' }}>
                            {/* Background Image */}
                            {show.banner_image && (
                                <div
                                    className="bg-image"
                                    style={{
                                        backgroundImage: `url(${show.banner_image})`,
                                        filter: 'blur(8px)',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        zIndex: -1,
                                    }}
                                ></div>
                            )}
                            <Card.Header className="bg-primary text-white text-center">
                                <h3>
                                    <FontAwesomeIcon icon={faGamepad} className="mr-2" /> Register Your Game With{' '}
                                    {show?.name}
                                </h3>
                            </Card.Header>
                            <Card.Body className="p-2" style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
                                {/* Description */}
                                <div className="mb-4 container">
                                    <p
                                        className="lead"
                                        dangerouslySetInnerHTML={{ __html: show?.description_game_registration }}
                                    />
                                </div>

                                {/* Registration Form and Success Message */}
                                {isRegistered ? (
                                    <Alert variant="success" className="text-center">
                                        {successMessage}
                                    </Alert>
                                ) : (
                                    <>
                                        {successMessage && <Alert variant="success">{successMessage}</Alert>}
                                        <GameTitleForm
                                            gameTitle={gameTitle}
                                            campaignData={{}}
                                            setGameTitle={setGameTitle}
                                            setMainImageBlob={setMainImageBlob}
                                            setBannerImageBlob={setBannerImageBlob}
                                            errors={titleErrors}
                                            externalGameData={externalGameTitle}
                                            isCreate={true}
                                            showPressKit={true}
                                        />
                                        {Object.keys(titleErrors).map((errorKey) =>
                                            titleErrors[errorKey].map((message, index) => (
                                                <Danger key={`title-${errorKey}-${index}`} message={message} />
                                            ))
                                        )}
                                        <div className="text-center mt-4">
                                            <Button
                                                onClick={handleSubmit}
                                                variant="primary"
                                                size="lg"
                                                className="px-5"
                                                disabled={isLoading}
                                            >
                                                {isLoading ? <Loading /> : 'Register Game'}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </Card.Body>

                            {/* Login/Register Overlay */}
                            {!Glitch.util.Session.isLoggedIn() && (
                                <div
                                    className="overlay d-flex flex-column justify-content-center align-items-center"
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                        color: 'white',
                                        zIndex: 2,
                                        textAlign: 'center',
                                        padding: '20px',
                                    }}
                                >
                                    <h2>Please Log In or Register to Continue</h2>
                                    <p className="lead">
                                        You need an account to register your game for {show?.name}.
                                    </p>
                                    <div className="mt-4">
                                        <Button
                                            variant="light"
                                            className="mx-2"
                                            onClick={handleLoginRedirect}
                                        >
                                            <FontAwesomeIcon icon={faLock} className="mr-2" />
                                            Log In
                                        </Button>
                                        <Button
                                            variant="success"
                                            className="mx-2"
                                            onClick={handleRegisterRedirect}
                                        >
                                            <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                                            Register
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>
            </Container>
            <Footer />

            {/* Login/Register Modal for Mobile Devices */}
            <Modal
                show={showLoginModal && !Glitch.util.Session.isLoggedIn()}
                onHide={() => setShowLoginModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title className='text-black'>Please Log In or Register</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className='text-black'>You need an account to register your game for {show?.name}.</p>
                    <div className="text-center mt-4">
                        <Button variant="primary" className="mx-2" onClick={handleLoginRedirect}>
                            <FontAwesomeIcon icon={faLock} className="mr-2" />
                            Log In
                        </Button>
                        <Button variant="success" className="mx-2" onClick={handleRegisterRedirect}>
                            <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                            Register
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </Fragment>
    );
};

export default GameShowRegisterGameStandalonePage;
